import type { QRL } from "@builder.io/qwik";
import { $, component$, Slot } from "@builder.io/qwik";
import {
  LuAlertTriangle,
  LuInfo,
  LuLoader2,
  LuWand,
} from "@qwikest/icons/lucide";
import { TooltipTrigger, TooltipContent, Tooltip } from "./tooltip";
import { Label } from "./label";
import { PrivateIcon } from "./private-icon";
import { useAiFillField } from "~/lib/actions/ai-fill-field";
import { cn } from "~/lib/cn";

export const ErrorMessage = component$<{ error?: string }>(({ error }) => (
  <>{error && <div class="text-red-8 text-xs">{error}</div>}</>
));

export const HelpText = component$(({ text }: { text: string }) => (
  <Tooltip>
    <TooltipTrigger>
      <LuInfo class="text-tertiary cursor-help text-xs" />
    </TooltipTrigger>
    <TooltipContent>{text}</TooltipContent>
  </Tooltip>
));

export const FormField = component$(
  ({
    label,
    labelSize = "sm",
    required,
    helpText,
    error,
    checkbox,
    isPrivate,
    fullWidth,
    maxLength,
    onAiFillField$,
  }: {
    label?: string;
    labelSize?: "sm" | "md" | "lg" | "xl";
    required?: boolean;
    helpText?: string;
    error?: string;
    checkbox?: boolean;
    isPrivate?: boolean;
    fullWidth?: boolean;
    maxLength?: number;
    onAiFillField$?: QRL<(value: string) => void>;
  }) => {
    const aiFillField = useAiFillField();

    return (
      <div
        class={[
          "flex gap-2",
          {
            "flex-1": fullWidth,
            "flex-col": !checkbox,
            "flex-row-reverse justify-end": checkbox,
          },
        ]}
      >
        {label && (
          <div
            class={cn("flex items-center gap-2", {
              "mb-4": labelSize === "xl",
            })}
          >
            {isPrivate && <PrivateIcon class="text-xs" />}
            <Label
              for={label.toLowerCase().replace(/\s/g, "-")}
              size={labelSize}
              class={[
                "text-primary",
                {
                  "text-red-700": error,
                },
              ]}
            >
              {label}
              {required && <span class="text-red-500">*</span>}
            </Label>
            {helpText && <HelpText text={helpText} />}
            {onAiFillField$ && (
              <Tooltip>
                <TooltipTrigger
                  class="text-tertiary hover:text-secondary flex h-6 w-6 shrink-0 grow-0 items-center justify-center rounded-full text-sm hover:bg-gray-100"
                  disabled={aiFillField.isRunning}
                  type="button"
                  onClick$={$(async () => {
                    const result = await aiFillField.submit({
                      label,
                      helpText,
                      maxLength,
                    });
                    if (result.value.content) {
                      await onAiFillField$(result.value.content);
                    } else {
                      console.error(
                        `Failed to generate content for field ${label}`,
                        result,
                      );
                    }
                  })}
                >
                  {aiFillField.isRunning ? (
                    <LuLoader2 class="animate-spin" />
                  ) : (aiFillField.status ?? 0) >= 400 ? (
                    <LuAlertTriangle />
                  ) : (
                    <LuWand />
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  Generate content for this field using AI
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        <div class="flex items-start justify-stretch gap-2">
          <div class="grow">
            <Slot />
          </div>
        </div>
        <ErrorMessage error={error} />
      </div>
    );
  },
);
